/**
 * Panel Resizer - Enables drag-to-resize for side panels
 *
 * Adds a resize handle to the left edge of panels (chat, process, templates, etc.)
 * that allows users to adjust panel width by dragging.
 */

export class PanelResizer {
  constructor() {
    this.resizablePanels = [
      'chat-interface',
      'obsidian-vault-interface'
    ];

    this.isResizing = false;
    this.currentPanel = null;
    this.startX = 0;
    this.startWidth = 0;
    this.minWidth = 280;
    this.minEditorWidth = 150;
    this.stylesApplied = false;

    // Bind methods
    this.onMouseMove = this.onMouseMove.bind(this);
    this.onMouseUp = this.onMouseUp.bind(this);
  }

  /**
   * Initialize resize handles for all panels
   */
  init() {
    this.resizablePanels.forEach(panelId => {
      const panel = document.getElementById(panelId);
      if (panel) {
        this.addResizeHandle(panel);
      }
    });

    // Add global mouse event listeners
    document.addEventListener('mousemove', this.onMouseMove);
    document.addEventListener('mouseup', this.onMouseUp);
  }

  /**
   * Add a resize handle to a panel
   */
  addResizeHandle(panel) {
    // Check if handle already exists
    if (panel.querySelector('.panel-resize-handle')) {
      return;
    }

    const handle = document.createElement('div');
    handle.className = 'panel-resize-handle';
    handle.title = 'Ziehen zum Ändern der Breite';

    // Insert handle as first child
    panel.insertBefore(handle, panel.firstChild);

    // Add mousedown listener
    handle.addEventListener('mousedown', (e) => {
      this.startResize(e, panel);
    });
  }

  /**
   * Start resizing
   */
  startResize(e, panel) {
    e.preventDefault();
    e.stopPropagation();

    this.isResizing = true;
    this.currentPanel = panel;
    this.startX = e.clientX;
    this.stylesApplied = false;

    // Use getBoundingClientRect for actual visual dimensions
    const panelRect = panel.getBoundingClientRect();
    this.startWidth = Math.round(panelRect.width);

    // Store the panel's right edge position - this MUST stay fixed
    this.startPanelRight = panelRect.right;

    // Get container for max width calculation. clientWidth includes padding,
    // but flex children lay out inside the content box — subtract padding.
    this.container = document.querySelector('.split-container');
    if (this.container) {
      const cs = getComputedStyle(this.container);
      const padX = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
      this.containerWidth = Math.max(0, this.container.clientWidth - padX);
    }

    // Add visual feedback classes only
    panel.classList.add('resizing');
    document.body.classList.add('panel-resizing');
  }

  /**
   * Handle mouse move during resize
   */
  onMouseMove(e) {
    if (!this.isResizing || !this.currentPanel) {
      return;
    }

    e.preventDefault();

    // Calculate delta from start position (moving left = positive delta = wider panel)
    const deltaX = this.startX - e.clientX;

    // Only apply styles if there's actual movement (more than 2px threshold)
    if (!this.stylesApplied && Math.abs(deltaX) < 2) {
      return;
    }

    // Calculate max panel width. The vault panel is allowed past the 75%
    // cap *only* when it sits next to the editor (split-with-vault layout),
    // because the editor can shrink with flex. In other layouts like
    // openWithChat() the sibling panel has `flex: 0 0 calc(50% - 10px)` and
    // will not shrink, so growing the vault beyond the 75% cap would
    // overflow the container. Fall back to the historical cap there.
    const gap = 20;
    const containerW = this.containerWidth || 1000;
    const isVault = this.currentPanel && this.currentPanel.id === 'obsidian-vault-interface';
    const editorPanel = document.getElementById('markdown-editor');
    const editorVisible = !!(editorPanel && editorPanel.offsetParent !== null);
    const vaultNextToEditor = editorVisible && editorPanel.classList.contains('split-with-vault');
    const editorSpace = containerW - this.minEditorWidth - gap;
    // Vault next to editor may grow to the full container width (editor can
    // shrink to 0 via `flex: 0 1 150px; min-width: 0`). Other layouts keep
    // the historical 75% cap and leave room for the sibling panel.
    const maxPanelWidth = (isVault && vaultNextToEditor)
      ? containerW - gap
      : Math.min(containerW * 0.75, editorSpace);

    // Calculate new panel width
    let newPanelWidth = this.startWidth + deltaX;

    // Clamp panel width
    newPanelWidth = Math.max(this.minWidth, Math.min(maxPanelWidth, newPanelWidth));

    // Skip if width hasn't changed (at min or max boundary)
    if (this.lastAppliedWidth === newPanelWidth) {
      return;
    }
    this.lastAppliedWidth = newPanelWidth;

    // On first significant movement, add class
    if (!this.stylesApplied) {
      this.currentPanel.classList.add('custom-width');
      // Clear any existing transform
      this.currentPanel.style.transform = '';
      this.accumulatedTransform = 0;
      this.stylesApplied = true;
    }

    // Remember right edge before width change
    const rightBefore = this.currentPanel.getBoundingClientRect().right;

    // Set panel width via flex (stays in normal flow)
    this.currentPanel.style.width = `${newPanelWidth}px`;
    this.currentPanel.style.maxWidth = `${newPanelWidth}px`;
    this.currentPanel.style.flex = `0 0 ${newPanelWidth}px`;

    // Force layout recalc and measure drift
    const rightAfter = this.currentPanel.getBoundingClientRect().right;
    const frameDrift = rightAfter - rightBefore;

    // Accumulate the drift and apply as transform
    if (Math.abs(frameDrift) > 0.1) {
      this.accumulatedTransform = (this.accumulatedTransform || 0) + frameDrift;
      this.currentPanel.style.transform = `translateX(${-this.accumulatedTransform}px)`;
    }
  }

  /**
   * Handle mouse up - stop resizing
   */
  onMouseUp() {
    if (!this.isResizing) {
      return;
    }

    // Remove resizing classes
    if (this.currentPanel) {
      this.currentPanel.classList.remove('resizing');

      // Only save if styles were actually applied (user dragged)
      if (this.stylesApplied) {
        // Get the final width (from the style, not visual)
        const finalWidth = parseInt(this.currentPanel.style.width) || this.startWidth;
        const widthPx = `${finalWidth}px`;

        // Keep panel at final width
        this.currentPanel.style.width = widthPx;
        this.currentPanel.style.maxWidth = widthPx;
        this.currentPanel.style.flex = `0 0 ${widthPx}`;

        // Keep the transform to maintain visual position
        // Store it so we can restore it if needed
        const currentTransform = this.accumulatedTransform || 0;
        if (Math.abs(currentTransform) > 0.5) {
          this.currentPanel.style.transform = `translateX(${-currentTransform}px)`;
          this.currentPanel.dataset.resizeTransform = currentTransform;
        } else {
          this.currentPanel.style.transform = '';
          delete this.currentPanel.dataset.resizeTransform;
        }

        // Save width to localStorage for persistence
        const panelId = this.currentPanel.id;
        if (panelId) {
          localStorage.setItem(`panel-width-${panelId}`, widthPx);
          this.currentPanel.dataset.customWidth = widthPx;
        }
      }
    }

    // Clean up references and state
    this.container = null;
    this.lastAppliedWidth = null;
    this.accumulatedTransform = 0;

    document.body.classList.remove('panel-resizing');

    this.isResizing = false;
    this.currentPanel = null;
    this.stylesApplied = false;
  }

  /**
   * Restore saved panel widths from localStorage
   * Note: We only store the width, but don't auto-apply it.
   * Custom widths are only applied during active resize.
   */
  restoreSavedWidths() {
    // Just observe visibility changes, don't auto-apply saved widths
    // This prevents layout shifts when panels open normally
    this.observePanelVisibility();
  }

  /**
   * Observe panel visibility changes to clean up custom widths when panels close
   */
  observePanelVisibility() {
    let isApplying = false; // Prevent infinite loop

    const observer = new MutationObserver((mutations) => {
      if (isApplying) return; // Skip if we're currently applying styles

      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          const panel = mutation.target;

          // Panel was hidden - reset its custom styles and editor styles
          if (!panel.classList.contains('visible') && panel.classList.contains('custom-width')) {
            isApplying = true;
            panel.classList.remove('custom-width');
            // Clear all inline styles including transform
            panel.style.cssText = '';
            panel.style.transform = '';
            delete panel.dataset.customWidth;
            delete panel.dataset.resizeTransform;
            this.resetContainerStyles();
            this.resetEditorStyles();
            setTimeout(() => { isApplying = false; }, 50);
          }
        }
      });
    });

    this.resizablePanels.forEach(panelId => {
      const panel = document.getElementById(panelId);
      if (panel) {
        observer.observe(panel, { attributes: true, attributeFilter: ['class'] });
      }
    });
  }

  /**
   * Reset container styles to default (restore centering)
   */
  resetContainerStyles() {
    // Check if any panel still has custom-width
    const anyCustomWidth = this.resizablePanels.some(panelId => {
      const panel = document.getElementById(panelId);
      return panel && panel.classList.contains('custom-width');
    });

    // Only reset container if no panels have custom width
    if (!anyCustomWidth) {
      const container = document.querySelector('.split-container');
      if (container) {
        container.style.marginLeft = '';
        container.style.marginRight = '';
      }
    }
  }

  /**
   * Reset editor panel styles to default
   */
  resetEditorStyles() {
    const editorPanel = document.getElementById('markdown-editor');
    if (editorPanel) {
      editorPanel.style.cssText = '';
    }
  }

  /**
   * Apply custom width to a panel
   */
  applyCustomWidth(panel, width) {
    panel.classList.add('custom-width');
    panel.style.cssText = `
      width: ${width} !important;
      max-width: ${width} !important;
      flex: 0 0 ${width} !important;
      display: flex !important;
    `;

    // Adjust editor - must override all constraints
    const editorPanel = document.getElementById('markdown-editor');
    if (editorPanel) {
      editorPanel.style.cssText = `
        flex: 1 1 auto !important;
        min-width: 150px !important;
        max-width: none !important;
        width: auto !important;
      `;
    }
  }

  /**
   * Reset panel width to default
   */
  resetPanelWidth(panelId) {
    const panel = document.getElementById(panelId);
    if (panel) {
      panel.classList.remove('custom-width');
      panel.style.cssText = '';
      panel.style.transform = '';
      delete panel.dataset.customWidth;
      delete panel.dataset.resizeTransform;
      localStorage.removeItem(`panel-width-${panelId}`);

      // Reset editor panel
      const editorPanel = document.getElementById('markdown-editor');
      if (editorPanel) {
        editorPanel.style.cssText = '';
      }

      // Reset container
      this.resetContainerStyles();
    }
  }

  /**
   * Reset all panel widths to default
   */
  resetAllPanelWidths() {
    this.resizablePanels.forEach(panelId => {
      this.resetPanelWidth(panelId);
    });
  }
}

export default PanelResizer;
